import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FiZap, FiEye, FiSearch, FiX } from 'react-icons/fi';
import { markOnboardingStep } from '../utils/onboarding';
import AppLayout from '../components/layout/AppLayout';
import Icon from '../components/common/Icon';
import PageHeader from '../components/common/PageHeader';
import Chip from '../components/common/Chip';
import DocumentDraftModal from '../components/drafts/DocumentDraftModal';
import { DOCUMENTS, DOC_CATEGORIES } from '../data/documents';
import { useApp } from '../context/AppContext';

const TAG_STYLES = {
  notice: { bg: '#edeef3', color: '#3a4a66', border: 'rgba(58, 74, 102, 0.15)' },
  form: { bg: '#fbf5e6', color: '#8a6a1f', border: 'rgba(138, 106, 31, 0.15)' },
  letter: { bg: '#fbf0ed', color: '#9a564a', border: 'rgba(154, 86, 74, 0.15)' },
  agreement: { bg: '#f5f0fa', color: '#6a1b9a', border: 'rgba(106, 27, 154, 0.15)' },
  certificate: { bg: '#edf7ed', color: '#2e7d32', border: 'rgba(46, 125, 50, 0.15)' },
  affidavit: { bg: '#edf6fc', color: '#1565c0', border: 'rgba(21, 101, 192, 0.15)' },
};

function DocCard({ doc, onDraft, onPreview }) {
  const tagStyle = TAG_STYLES[doc.category] || TAG_STYLES.notice;

  return (
    <div
      className="cs-resolution-card group rounded-[14px] px-4 py-3.5 cursor-pointer flex flex-col justify-between"
      onClick={() => onDraft(doc)}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span
            className="shrink-0 grid place-items-center w-8 h-8 rounded-[9px] border transition-all duration-300 group-hover:scale-105"
            style={{ background: tagStyle.bg, color: tagStyle.color, borderColor: tagStyle.border }}
          >
            <Icon name={doc.icon} size={15} />
          </span>
          <Chip bg={tagStyle.bg} color={tagStyle.color} className="uppercase tracking-[0.5px] !text-[9px] !px-2 !font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {doc.tag}
          </Chip>
        </div>
        <div>
          <div
            className="text-[13px] font-bold leading-[1.3] text-[#08111f] mb-0.5 transition-colors group-hover:text-[#8a6a1f]"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}
          >
            {doc.title}
          </div>
          <div
            className="text-[10.5px] leading-[1.4] text-[#7a7060] font-medium"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {doc.section}
          </div>
        </div>
      </div>

      <div
        className="flex items-center gap-2 mt-3 pt-2.5 border-t border-[#f2eee6]"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          title="AI Draft"
          onClick={() => onDraft(doc)}
          className="flex items-center justify-center w-7 h-7 rounded-[8px] border border-[#ddd6c6] bg-white text-[#8a6a1f] hover:bg-[#faf7f2] hover:border-[#cdc4b0] transition-all active:scale-95"
        >
          <FiZap size={13} />
        </button>
        <button
          type="button"
          title="Preview Document"
          onClick={() => onPreview(doc)}
          className="flex items-center justify-center w-7 h-7 rounded-[8px] border border-[#ddd6c6] bg-white text-[#5a5145] hover:bg-[#faf7f2] hover:border-[#cdc4b0] transition-all active:scale-95"
        >
          <FiEye size={13} />
        </button>
      </div>
    </div>
  );
}

export default function DocumentLibraryPage() {
  const location = useLocation();
  // Getting-started runway: reaching any drafting surface completes the step.
  useEffect(() => { markOnboardingStep('visited-drafting'); }, []);
  const { activeCompany } = useApp();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Deep-link: open a specific document from spotlight search
  useEffect(() => {
    const docId = location.state?.documentId;
    if (!docId) return;
    const found = DOCUMENTS.find((d) => d.id === docId);
    if (found) setSelected({ doc: found });
    window.history.replaceState({}, '');
  }, [location.state?.documentId]);

  const filtered = useMemo(() => {
    return DOCUMENTS.filter((doc) => {
      const matchesCategory = activeCategory === 'all' || doc.category === activeCategory;
      const matchesSearch = 
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.section.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.tag.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const hasResults = filtered.length > 0;

  return (
    <AppLayout pageTitle="Document Library">
      <PageHeader
        title={(
          <span className="inline-flex items-center align-middle">
            Document Library
            <span
              className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[1px] px-[7px] py-[2px] rounded-[5px] ml-[7px] align-middle"
              style={{ background: 'linear-gradient(135deg, #b8973a, #d4820a)', color: '#08111f' }}
            >
              <Icon name="✨" size={10} />AI
            </span>
          </span>
        )}
        subtitle="Notices, forms, letters, agreements & more · AI drafts with company letterhead · Every document fully editable, printable & downloadable"
      />

      {/* Embedded Quick Search Row */}
      <div className="relative mb-4">
        <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7a7060]" size={16} />
        <input
          type="text"
          placeholder="Search through document library templates by name, type, or regulation..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-[38px] pr-9 py-2.5 rounded-xl border-[1.5px] text-[13px] outline-none transition-all focus:border-[#145f5f] focus:shadow-sm"
          style={{ borderColor: '#ddd6c6', background: '#ffffff', fontFamily: "'Outfit', sans-serif" }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#7a7060] hover:text-[#08111f]"
          >
            <FiX size={15} />
          </button>
        )}
      </div>

      {/* Category Tabs Pill Selector */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 cs-premium-scrollbar">
        {DOC_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-3.5 py-1.5 rounded-xl text-[12px] font-bold transition-all whitespace-nowrap border ${
              activeCategory === cat.id
                ? 'bg-[#145f5f] border-[#145f5f] text-white shadow-sm'
                : 'bg-white border-[#e2d9c8] text-[#5a5145] hover:bg-[#faf9f6]'
            }`}
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Document Templates Grid */}
      {hasResults ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {filtered.map((doc) => (
            <DocCard
              key={doc.id}
              doc={doc}
              onDraft={(item) => setSelected({ doc: item })}
              onPreview={(item) => setSelected({ doc: item })}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-2xl border" style={{ borderColor: '#e2d9c8' }}>
          <div className="text-[28px] mb-2">🔍</div>
          <h3 className="text-[14px] font-bold text-[#08111f]">No templates found</h3>
          <p className="text-[12px] text-[#7a7060] mt-1">
            We couldn't find any documents matching "{searchQuery}". Try adjusting your keywords.
          </p>
          <button
            onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
            className="mt-4 px-4 py-2 bg-[#fbf5e6] text-[#8a6a1f] border border-[#8a6a1f]/20 rounded-xl text-[12px] font-semibold hover:bg-[#faf4e2]"
          >
            Clear filters
          </button>
        </div>
      )}

      <DocumentDraftModal
        selected={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        pageOrigin="document-library"
        companyCin={activeCompany?.cin}
        companyName={activeCompany?.name}
      />
    </AppLayout>
  );
}
